import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkspaceSidebar from '../../components/ui/WorkspaceSidebar';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuestionCard from './components/QuestionCard';
import ContextPanel from './components/ContextPanel';
import AISidecar from './components/AISidecar';
import ProgressIndicator from './components/ProgressIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const QuestionnaireRunner = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [aiSidecarOpen, setAiSidecarOpen] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');

  // Mock data for organization profile
  const organizationProfile = {
    name: "Enterprise Solutions Ltd",
    industry: "Manufacturing",
    size: "5,000+ employees",
    region: "North America",
    currentSystems: [
      "SAP ECC 6.0",
      "Legacy WMS",
      "Custom Finance System",
      "Third-party CRM"
    ]
  };

  // Mock data for decision history
  const decisionHistory = [
    {
      id: 1,
      questionId: 1,
      question: "What is your primary business model?",
      response: "Make-to-Order Manufacturing",
      timestamp: "10:15 AM",
      impact: "Affects inventory management and production planning processes"
    },
    {
      id: 2,
      questionId: 3,
      question: "Do you require multi-currency support?",
      response: "Yes, USD, EUR, CAD",
      timestamp: "10:18 AM",
      impact: "Enables global operations and financial consolidation"
    },
    {
      id: 3,
      questionId: 5,
      question: "What approval levels are needed for purchase orders?",
      response: "3-tier approval (Manager, Director, VP)",
      timestamp: "10:22 AM",
      impact: "Defines workflow configuration and authorization matrix"
    }
  ];

  // Mock data for best practice information
  const bestPracticeInfo = {
    code: "1QM",
    title: "Quality Management in Discrete Manufacturing",
    description: "Comprehensive quality management processes for discrete manufacturing environments including inspection planning, quality notifications, and certificates.",
    version: "S/4HANA 2023",
    lineOfBusiness: "Manufacturing",
    processes: [
      "Inspection Planning",
      "Quality Notifications",
      "Quality Certificates",
      "Statistical Process Control",
      "Supplier Quality Management"
    ],
    dependencies: [
      "Material Management (MM)",
      "Production Planning (PP)",
      "Plant Maintenance (PM)"
    ]
  };

  // Mock questionnaire sections and questions
  const questionnaireSections = [
    {
      id: 'business-model',
      name: 'Business Model',
      description: 'Core business processes and operations',
      total: 8,
      completed: 3
    },
    {
      id: 'organizational',
      name: 'Organizational Structure',
      description: 'Company structure and hierarchies',
      total: 6,
      completed: 0
    },
    {
      id: 'technical',
      name: 'Technical Requirements',
      description: 'System integration and technical needs',
      total: 10,
      completed: 0
    },
    {
      id: 'compliance',
      name: 'Compliance & Governance',
      description: 'Regulatory and governance requirements',
      total: 5,
      completed: 0
    }
  ];

  const questions = [
    // Business Model Section
    {
      id: 'bm-1',
      section: 'business-model',
      sequence: 1,
      category: 'Business Process',
      sapProcess: 'Order-to-Cash',
      type: 'multiple_choice',
      title: 'What is your primary manufacturing strategy?',
      description: 'This determines the core production planning and inventory management approach.',
      required: true,
      allowMultiple: false,
      options: [
        {
          id: 'mto',
          label: 'Make-to-Order (MTO)',
          description: 'Products are manufactured after receiving customer orders'
        },
        {
          id: 'mts',
          label: 'Make-to-Stock (MTS)',
          description: 'Products are manufactured based on demand forecasts'
        },
        {
          id: 'ato',
          label: 'Assemble-to-Order (ATO)',
          description: 'Final assembly occurs after receiving customer orders'
        },
        {
          id: 'eto',
          label: 'Engineer-to-Order (ETO)',
          description: 'Products are designed and manufactured per customer specifications'
        }
      ]
    },
    {
      id: 'bm-2',
      section: 'business-model',
      sequence: 2,
      category: 'Financial',
      sapProcess: 'Financial Accounting',
      type: 'multiple_choice',
      title: 'Which currencies does your organization operate in?',
      description: 'This affects currency configuration and financial reporting requirements.',
      required: true,
      allowMultiple: true,
      options: [
        { id: 'usd', label: 'US Dollar (USD)', description: 'Primary currency for North American operations' },
        { id: 'eur', label: 'Euro (EUR)', description: 'European operations currency' },
        { id: 'cad', label: 'Canadian Dollar (CAD)', description: 'Canadian operations currency' },
        { id: 'gbp', label: 'British Pound (GBP)', description: 'UK operations currency' },
        { id: 'jpy', label: 'Japanese Yen (JPY)', description: 'Asian Pacific operations currency' }
      ]
    },
    {
      id: 'bm-3',
      section: 'business-model',
      sequence: 3,
      category: 'Procurement',
      sapProcess: 'Procure-to-Pay',
      type: 'number',
      title: 'What is your typical purchase order approval threshold (in USD)?',
      description: 'This determines the workflow configuration for purchase order approvals.',
      required: true,
      placeholder: 'Enter amount in USD',
      min: 0,
      max: 10000000
    },
    {
      id: 'bm-4',
      section: 'business-model',
      sequence: 4,
      category: 'Quality',
      sapProcess: 'Quality Management',
      type: 'textarea',
      title: 'Describe your quality inspection requirements',
      description: 'Detail the quality control processes and inspection points in your manufacturing workflow.',
      required: true,
      placeholder: 'Describe inspection processes, quality gates, and compliance requirements...'
    },
    // Organizational Section
    {
      id: 'org-1',
      section: 'organizational',
      sequence: 5,
      category: 'Structure',
      sapProcess: 'Organizational Management',
      type: 'multiple_choice',
      title: 'How many legal entities does your organization have?',
      description: 'This affects company code configuration and legal reporting requirements.',
      required: true,
      allowMultiple: false,
      options: [
        { id: 'single', label: 'Single Legal Entity', description: 'One company code configuration' },
        { id: 'multiple-domestic', label: '2-5 Domestic Entities', description: 'Multiple entities within one country' },
        { id: 'multiple-international', label: '5+ International Entities', description: 'Complex multi-country structure' }
      ]
    }
  ];

  const currentSection = questionnaireSections?.[currentSectionIndex];
  const sectionQuestions = questions?.filter(q => q?.section === currentSection?.id);
  const currentQuestion = sectionQuestions?.[currentQuestionIndex];
  
  const totalQuestions = questions?.length;
  const completedQuestions = Object.keys(responses)?.length;

  // Auto-save functionality
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (Object.keys(responses)?.length > 0) {
        setAutoSaveStatus('saving');
        // Simulate save
        setTimeout(() => {
          setAutoSaveStatus('saved');
        }, 1000);
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [responses]);

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { value, timestamp: new Date()?.toISOString() }
    }));
    setAutoSaveStatus('unsaved');
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < sectionQuestions?.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (currentSectionIndex < questionnaireSections?.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      setCurrentQuestionIndex(0);
    } else {
      // Questionnaire complete
      navigate('/fit-gap-board');
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
      const prevSection = questionnaireSections?.[currentSectionIndex - 1];
      const prevSectionQuestions = questions?.filter(q => q?.section === prevSection?.id);
      setCurrentQuestionIndex(prevSectionQuestions?.length - 1);
    }
  };

  const handleSectionChange = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentQuestionIndex(0);
  };

  const isQuestionValid = () => {
    if (!currentQuestion?.required) return true;
    const response = responses?.[currentQuestion?.id];
    if (!response) return false;
    
    if (currentQuestion?.type === 'multiple_choice' && currentQuestion?.allowMultiple) {
      return Array.isArray(response?.value) && response?.value?.length > 0;
    }
    
    return response?.value !== undefined && response?.value !== '';
  };

  const isFirstQuestion = currentSectionIndex === 0 && currentQuestionIndex === 0;
  const isLastQuestion = currentSectionIndex === questionnaireSections?.length - 1 && 
                        currentQuestionIndex === sectionQuestions?.length - 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Workspace Sidebar */}
      <WorkspaceSidebar 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      {/* Main Content Area */}
      <div className={`${sidebarCollapsed ? 'ml-16' : 'ml-64'} ${aiSidecarOpen ? 'mr-96' : 'mr-0'} nav-transition`}>
        {/* Top Header */}
        <header className="fixed top-0 right-0 left-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border">
          <div className={`${sidebarCollapsed ? 'ml-16' : 'ml-64'} ${aiSidecarOpen ? 'mr-96' : 'mr-0'} nav-transition`}>
            <div className="flex items-center justify-between h-16 px-6">
              <div className="flex items-center space-x-4">
                <h1 className="text-xl font-semibold text-foreground">
                  Questionnaire Runner
                </h1>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="FileQuestion" size={16} />
                  <span>{currentSection?.name}</span>
                  <span>•</span>
                  <span>Question {currentQuestion?.sequence}</span>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Auto-save Status */}
                <div className="flex items-center space-x-2 text-sm">
                  {autoSaveStatus === 'saving' && (
                    <>
                      <Icon name="Loader2" size={14} className="animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">Saving...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <Icon name="Check" size={14} className="text-success" />
                      <span className="text-success">Saved</span>
                    </>
                  )}
                  {autoSaveStatus === 'unsaved' && (
                    <>
                      <Icon name="Circle" size={14} className="text-warning" />
                      <span className="text-warning">Unsaved changes</span>
                    </>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAiSidecarOpen(!aiSidecarOpen)}
                  iconName="Bot"
                  iconPosition="left"
                >
                  AI Assistant
                </Button>

                <UserProfileDropdown />
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="pt-16">
          <div className="flex">
            {/* Context Panel */}
            <ContextPanel
              organizationProfile={organizationProfile}
              decisionHistory={decisionHistory}
              bestPracticeInfo={bestPracticeInfo}
              isCollapsed={contextCollapsed}
              onToggle={() => setContextCollapsed(!contextCollapsed)}
            />

            {/* Main Question Area */}
            <div className="flex-1 p-6">
              <div className="max-w-4xl mx-auto">
                <BreadcrumbNavigation />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Question Card */}
                  <div className="lg:col-span-2">
                    {currentQuestion && (
                      <QuestionCard
                        question={currentQuestion}
                        response={responses?.[currentQuestion?.id]}
                        onResponseChange={handleResponseChange}
                        onNext={handleNextQuestion}
                        onPrevious={handlePreviousQuestion}
                        isFirst={isFirstQuestion}
                        isLast={isLastQuestion}
                        isValid={isQuestionValid()}
                      />
                    )}
                  </div>

                  {/* Progress Indicator */}
                  <div className="lg:col-span-1">
                    <ProgressIndicator
                      sections={questionnaireSections}
                      currentSection={currentSectionIndex}
                      currentQuestion={currentQuestionIndex}
                      totalQuestions={totalQuestions}
                      completedQuestions={completedQuestions}
                      onSectionChange={handleSectionChange}
                    />
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-8 flex items-center justify-between p-4 bg-card border border-border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/best-practice-picker')}
                      iconName="ArrowLeft"
                      iconPosition="left"
                    >
                      Back to Best Practices
                    </Button>
                    
                    <Button
                      variant="ghost"
                      iconName="Save"
                      iconPosition="left"
                    >
                      Export Progress
                    </Button>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      iconName="RotateCcw"
                      iconPosition="left"
                    >
                      Reset Section
                    </Button>
                    
                    <Button
                      variant="default"
                      onClick={() => navigate('/fit-gap-board')}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Continue to Fit/Gap Analysis
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* AI Sidecar */}
      <AISidecar
        isOpen={aiSidecarOpen}
        onToggle={() => setAiSidecarOpen(!aiSidecarOpen)}
        currentQuestion={currentQuestion}
      />
    </div>
  );
};

export default QuestionnaireRunner;